import React from "react";
import styled, { css } from "styled-components";
import { useLocation } from "@reach/router";
import loadable from "@loadable/component";

import { breakpoints, fontFamily, colors, sectionRhythm } from "../../../theme";
import { PageSection } from "../../components/layout/layout-styles";
import TestimonialsHeader from "./TestimonialsHeader";
import TestimonialsSkeleton from "./testimonials-skeleton";

const Testimonials = loadable(() => import("./Testimonials"));

const Section = styled(PageSection)`
  background-color: ${(props) =>
    props.bgColor ? props.bgColor : colors.white};
  overflow-x: clip;

  /* Extra bottom space before Contact - matches FaqSection */
  ${(props) =>
    props.$beforeContact &&
    css`
      padding-bottom: ${sectionRhythm.gap.mobile}px;

      @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
        padding-bottom: ${sectionRhythm.gap.desktop}px;
      }
    `}
`;

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 0 20px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: 0;
  }
`;

const SectionHeader = styled.div`
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-family: ${fontFamily.bold};
  font-size: 32px;
  line-height: 102%;
  margin: 0;
  color: ${colors.accentContent};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 40px;
  }
`;

const HelperText = styled.p`
  font-family: ${fontFamily.medium};
  font-size: 16px;
  line-height: 120%;
  margin: 8px 0 0;
  color: ${colors.body};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 20px;
    max-width: 65%;
  }
`;

const SecondaryHeaderBlock = styled.div`
  margin-top: 5em;
`;

const TestimonialsBlock = styled.div`
  margin-top: ${sectionRhythm.paddingY.mobile}px;

  @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
    margin-top: ${sectionRhythm.paddingY.desktop}px;
  }
`;

const ReviewsSection = ({
  compensateHeaderGap = false,
  beforeContact = false,
}) => {
  const { pathname } = useLocation();
  const isTestimonialsPage = pathname.replace(/\/+$/, "") === "/testimonials";

  return (
    <Section
      name="testimonials"
      as="section"
      $compensateHeaderGap={compensateHeaderGap}
      $beforeContact={beforeContact}
    >
      {isTestimonialsPage ? (
        <Container>
          <TestimonialsHeader />
          <SecondaryHeaderBlock>
            <SectionHeader>
              <SectionTitle>
                15+ projects, even more talk about our work
              </SectionTitle>
              <HelperText>
                We merge user-focused strategy with striking visuals to craft
                products that not only look exceptional but perform flawlessly.
                Every choice is made to engage your audience, improve usability,
                and drive measurable growth.
              </HelperText>
            </SectionHeader>
            <TestimonialsBlock>
              <Testimonials
                expanded
                fallback={<TestimonialsSkeleton expanded />}
              />
            </TestimonialsBlock>
          </SecondaryHeaderBlock>
        </Container>
      ) : (
        <>
          <Container>
            <SectionHeader>
              <SectionTitle>What people say</SectionTitle>
              <HelperText>
                Real words from the people who trusted me with their products -
                honest takes on how the work turned out and what it was like to
                build it together.
              </HelperText>
            </SectionHeader>
          </Container>
          <TestimonialsBlock>
            <Testimonials
              fullBleed
              fallback={<TestimonialsSkeleton fullBleed />}
            />
          </TestimonialsBlock>
        </>
      )}
    </Section>
  );
};

export default ReviewsSection;
